import { h } from 'preact';
import * as BaseComp from 'components/bolt/hub/hub.js';
import Store from 'store/store';
import { accessNested, joinClasses } from 'utils/utils';
import Hero from 'components/bolt/hub/components/hero/hero';
import Carousel from 'components/bolt/hub/components/carousel/carousel';
import SideButton from 'components/bolt/hub/components/sideButton/sideButton';
import HeroCarousel from 'components/bolt/hub/components/heroCarousel/heroCarousel';
import { getDynamicData } from 'utils/bolt/dynamicData';
import HTML from 'components/core/html/html';
import { isVideosPage } from 'utils/bolt';
import slugify from 'utils/slugify';
import s from 'components/bolt/toutVertical/toutVertical.sss';

export default class Hub extends BaseComp.default {

	constructor(props) {
		super(props);
	}

	transformButtons(buttons) {
		return buttons ? buttons.map(b => {
			if (b.link && b.link.split('#')[1] === 'tickets') {
				let newLink = b.link.split('#').shift();
				b.link = newLink += '-showtimes';
			}
			if (b.videoOverlayName) {
				b.videoOverlay = { video: { 
					name: b.videoOverlayName, 
					youtubeId: b.videoOverlayYoutubeId,
					thumbSrc: `https://i.ytimg.com/vi/${b.videoOverlayYoutubeId}/mqdefault.jpg`,
					thumbAria: b.videoOverlayName,
				} };
			}
			b.btnCopy = b.label;
			return b;
		}) : [];
	}

	async loadCustomData(dynamicData) {
		if (!dynamicData) {
			return;
		}

		const hero = dynamicData.heroCarousel || dynamicData.hero;

		this.heroCarousel = {
			hasGradient: true,
			content: hero?.map?.(slide => {
				if (!slide) {
					return;
				}
				return {
					id: slide.id,
					title: slide.title,
					description: slide.description,
					background: { desktop: slide.img, mobile: slide.mobileImg, alt: slide.alt },
					buttons: this.transformButtons(slide.buttons),
				};
			}).filter(e => e)
		};
		this.carouselsBefore = dynamicData.carouselsBefore?.map?.(carousel => {
			if (!carousel?.content?.length) {
				return;
			}
			return {
				heading: carousel.name,
				anchor: slugify(carousel.name),
				tag: carousel.tag,
				isCategory: carousel.isCategory,
				tiles: carousel.content.map(srcTile => srcTile && {
					title: srcTile.title,
					teaser: srcTile.description,
					asset: srcTile.asset,
					altText: srcTile.altText,
					tag: srcTile.tag,
					path: srcTile.link?.split('#')?.[0],
					buttons: srcTile.button && [
						{ btnCopy: srcTile.button, link: srcTile.link }
					]
				}).filter(e => e)
			};
		}).filter(e => e);
		this.carouselsAfter = dynamicData.carouselsAfter?.map?.(carousel => {
			if (!carousel?.content?.length) {
				return;
			}
			return {
				heading: carousel.name,
				anchor: slugify(carousel.name),
				tag: carousel.tag,
				isCategory: carousel.isCategory,
				tiles: carousel.content.map(srcTile => srcTile && {
					title: srcTile.title,
					teaser: srcTile.description,
					asset: srcTile.asset,
					altText: srcTile.altText,
					tag: srcTile.tag,
					path: srcTile.link?.split('#')?.[0],
					buttons: srcTile.button && [
						{ btnCopy: srcTile.button, link: srcTile.link }
					]
				}).filter(e => e)
			};
		}).filter(e => e);
		this.promoBanner = dynamicData.promo;
		this.forceUpdate();

		const hash = location.hash.slice(1);
		if (hash) {
			this.props.goToHash?.(hash);
		}
	}

	async componentDidMount(...args) {
		super.componentDidMount?.(...args);

		if (this.props.id === 'landingPage') {
			await this.loadCustomData(await getDynamicData('landing'));
		} else {
			const allGroupPages = await getDynamicData('group-pages');
			await this.loadCustomData(Object.values(allGroupPages || {}).find(p => p?.url === this.props.id));
		}
		this.recalculateElementsPositions();
		this.goToHash();
	}

	triggerButtonClick(data) {
		const { link, title, button, sameWindow } = data;
		Store.emit('TOUT_CLICK', 'vertical', 'tout', `${title} - ${button}`);
		if (link.startsWith('http')) {
			let link = document.createElement('a');
			link.href = data.link;
			link.target = sameWindow ? '_self' : '_blank';
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else if (link.startsWith('@')) {
			return Store.emit('OPEN_RESERVED_VARIABLE_LINK', link);
		} else if (link.startsWith('tickets/?id=')) {
			const movieId = link.replace('tickets/?id=', '').trim();
			this.openShowtimesAndFilterForMovieId(movieId);
		} else if (isVideosPage(link)) {
			return Store.emit('OPEN_VIDEO_OVERLAY', link);
		} else if (link === 'boltShowtimes') {
			return Store.emit('SHOWTIMES_SHOW', true);
		} else if (link === 'boltHomeEnt') {
			return Store.emit('HOMEENT_SHOW', true);
		} else {
			const linkIsSection = accessNested(this.props, `appData.pages.${link}.section`, false);
			if (linkIsSection) {
				return Store.emit('CHANGE_ACTIVE_SECTION', link);
			} else {
				return Store.emit('CLOSE_AND_NAVIGATE', link);
			}
		}
	}

	renderPromoBanner(data) {
		const { title, description, img, alt, button } = data;

		return (
			<div class={s.wrapper}>
				<div class={s.stack}>
					<div class={s.touts}>
						<div class={s.tout}>
							<img crossOrigin="anonymous" class={s.image} src={img} alt={alt} />
							<div class={s.content}>
								{title && <HTML tag="h3" markdown content={title} class={joinClasses(s.toutTitle, 'h4')} />}
								{description && (
									<HTML
										tag="h4"
										markdown
										content={description}
										class={joinClasses(s.toutDescription, s.showGradient, 'large')}
									/>
								)}
								{button && (
									<button
										class={joinClasses(s.button, 'mediumB')}
										onClick={() => this.triggerButtonClick(data)}
									>
										{button}
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	render({ store, id }) {
		const section = accessNested(store, `appData.pages.${id}.section`, '');
		const clientDimensions = accessNested(store, 'client.dimensions', {});
		const heroData = accessNested(store, `appData.pages.${id}.data.hero`, '');
		const fallbackFormat = accessNested(store, `appData.pages.${id}.data.alternativeImageFormat`, 'jpg');
		const sideButtonData = accessNested(store, `appData.pages.${id}.data.sideButton`, '');
		const imageRegionRoot = accessNested(store, 'appData.urls.roots.imageRegion', '');
		const useTileSlantyTag = accessNested(store, 'appData.options.carouselTilesUseSlantyTags', false);
		const desktopSolidHeader = accessNested(store, 'header.powsterHeader.desktopSolidHeader', false);
		const headerHeight = accessNested(store, 'client.headerHeight', '');
		const hubActivePercentage = accessNested(store, 'hubActivePercentage', null);
		const appData = accessNested(store, `appData.pages.${id}.data`, {});
		const client = accessNested(store, 'client', '');
		return (
			<div style={{ marginTop: (!section && desktopSolidHeader) ? headerHeight : '' }} data-section={id}>
				{this.heroCarousel ? <HeroCarousel appData={appData} data={this.heroCarousel} client={client} /> : (
					<Hero
						data={heroData}
						clientDimensions={clientDimensions}
						fallbackFormat={fallbackFormat}
						visible={hubActivePercentage >= 0.15}
						hide={hubActivePercentage < 0.15}
						store={store}
					/>
				)}
				{this.carouselsBefore?.map((carousel, i) => (
					<Carousel
						data={carousel}
						index={i}
						imageRegionRoot={imageRegionRoot}
						clientDimensions={clientDimensions}
						useTileSlantyTag={useTileSlantyTag}
						key={i}
						fallbackFormat={fallbackFormat}
					/>
				))}
				{!!this.promoBanner?.title?.trim?.() && this.renderPromoBanner(this.promoBanner)}
				<div>
					{this.carouselsAfter?.map((carousel, i) => (
						<Carousel
							data={carousel}
							index={i}
							imageRegionRoot={imageRegionRoot}
							clientDimensions={clientDimensions}
							useTileSlantyTag={useTileSlantyTag}
							key={i}
							fallbackFormat={fallbackFormat}
						/>
					))}
				</div>
				{sideButtonData && <SideButton data={sideButtonData} />}
				<div id="bottom" />
			</div>
		);
	}

}
